import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ErrorBoundary, WidgetErrorFallback } from "../../../components/ErrorBoundary";
import {
  StepsList,
  BusinessScoreWidget,
  RecommendedNextStep,
  MilestoneBanner,
} from "../../../../_start/partials/widgets";
import { CreateAppModal } from "../_modals/create-app-stepper/CreateAppModal";
import { LogoGeneratorModal } from "./LogoGeneratorModal";
import { WebsiteGeneratorModal } from "./WebsiteGeneratorModal";
import { SurveyBuilderModal } from "./SurveyBuilderModal";
import { MarketingPlanModal } from "./MarketingPlanModal";
import { MVPScaffolderModal } from "./MVPScaffolderModal";
import { SEOAgentModal } from "./SEOAgentModal";
import { ContentCreatorModal } from "./ContentCreatorModal";
import { EmailSequenceModal } from "./EmailSequenceModal";
import { SocialAssetsModal } from "./SocialAssetsModal";
import { PersonaTestModal } from "./PersonaTestModal";
import { db } from "../../../../config/firebase";
import { doc, collection, getDocs, getDoc, updateDoc, addDoc, query, orderBy, limit, where } from "firebase/firestore";
import { useBusinessAI } from "../../../../hooks/useBusinessAI";
import { useLogoGenerator } from "../../../../hooks/useLogoGenerator";
import { usePDFExport } from "../../../../hooks/usePDFExport";
import { useValidationExport } from "../../../../hooks/useValidationExport";
import { useBuildStatus } from "../../../../hooks/useBuildStatus";
import { useFeedbackCapture } from "../../../../hooks/useFeedbackCapture";
import { useSubscription } from "../../../../hooks/useSubscription";
import { usePublicReport } from "../../../../hooks/usePublicReport";
import { usePageData } from "../../../../_start/layout/core";
import { Team, Collaborator } from "../../../../types/team";
import { ShareProjectModal } from "../../../../components/team/ShareProjectModal";
import { TeamActivityFeed } from "../../team/TeamActivityFeed";
import { TeamProgressChart, StepAssignmentInfo } from "../../../../components/team/TeamProgressChart";
import { STEP_CONFIGS } from "../steps/stepConfig";

interface CategoryScores {
  marketOpportunity: number;
  customerUnderstanding: number;
  valueAndProduct: number;
  businessModel: number;
  validationExecution: number;
  [key: string]: number;
}

interface CategoryReasons {
  marketOpportunity?: string;
  customerUnderstanding?: string;
  valueAndProduct?: string;
  businessModel?: string;
  validationExecution?: string;
}

// DESIGN.md category colours for mini-map
const MINI_MAP_STEP_DATA: { title: string; color: string; lightColor: string }[] = [
  { title: "Market Segmentation",            color: "#3B82F6", lightColor: "#DBEAFE" },
  { title: "Select a Beachhead Market",       color: "#3B82F6", lightColor: "#DBEAFE" },
  { title: "Build an End User Profile",       color: "#3B82F6", lightColor: "#DBEAFE" },
  { title: "Calculate TAM for Beachhead",     color: "#3B82F6", lightColor: "#DBEAFE" },
  { title: "Profile the Persona",             color: "#3B82F6", lightColor: "#DBEAFE" },
  { title: "Full Life Cycle Use Case",        color: "#8B5CF6", lightColor: "#EDE9FE" },
  { title: "High-Level Product Spec",         color: "#8B5CF6", lightColor: "#EDE9FE" },
  { title: "Quantify the Value Proposition",  color: "#8B5CF6", lightColor: "#EDE9FE" },
  { title: "Identify Next 10 Customers",      color: "#8B5CF6", lightColor: "#EDE9FE" },
  { title: "Define Your Core",                color: "#8B5CF6", lightColor: "#EDE9FE" },
  { title: "Chart Competitive Position",      color: "#EC4899", lightColor: "#FCE7F3" },
  { title: "Determine Customer's DMU",        color: "#EC4899", lightColor: "#FCE7F3" },
  { title: "Map Process to Acquire Customer", color: "#EC4899", lightColor: "#FCE7F3" },
  { title: "Calculate TAM for Follow-on",     color: "#EC4899", lightColor: "#FCE7F3" },
  { title: "Design a Business Model",         color: "#F59E0B", lightColor: "#FEF3C7" },
  { title: "Set Your Pricing Framework",      color: "#F59E0B", lightColor: "#FEF3C7" },
  { title: "Calculate LTV",                   color: "#F59E0B", lightColor: "#FEF3C7" },
  { title: "Map the Sales Process",           color: "#F59E0B", lightColor: "#FEF3C7" },
  { title: "Calculate COCA",                  color: "#F59E0B", lightColor: "#FEF3C7" },
  { title: "Identify Key Assumptions",        color: "#10B981", lightColor: "#D1FAE5" },
  { title: "Test Key Assumptions",            color: "#10B981", lightColor: "#D1FAE5" },
  { title: "Define the MVP",                  color: "#10B981", lightColor: "#D1FAE5" },
  { title: "Show Dogs Will Eat the Food",     color: "#10B981", lightColor: "#D1FAE5" },
  { title: "Develop a Product Plan",          color: "#10B981", lightColor: "#D1FAE5" },
];

interface StepMiniMapProps {
  completionMap: Record<string, number>;
  notesMap: Record<string, boolean>;
  onStepClick: (stepNumber: number) => void;
}

const StepMiniMap: React.FC<StepMiniMapProps> = ({ completionMap, notesMap, onStepClick }) => (
  <div
    style={{
      background: "#FFFFFF",
      border: "1px solid #E2E8F0",
      borderRadius: "12px",
      padding: "20px 24px",
      marginBottom: "24px",
      boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
    }}
  >
    <div
      style={{
        fontSize: "13px",
        fontWeight: 600,
        color: "#475569",
        marginBottom: "12px",
        display: "flex",
        alignItems: "center",
        gap: "6px",
      }}
    >
      <i className="bi bi-grid-3x3" style={{ fontSize: "14px" }}></i>
      Step Progress
    </div>
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "8px",
      }}
    >
      {MINI_MAP_STEP_DATA.map((step, idx) => {
        const stepNum = idx + 1;
        const key = `step${stepNum}`;
        const isStarted = (completionMap[key] ?? 0) > 0;
        const hasNotes = isStarted && (notesMap[key] ?? false);
        return (
          <div key={key} style={{ position: "relative", flexShrink: 0 }}>
            <button
              type="button"
              onClick={() => onStepClick(stepNum)}
              title={`Step ${stepNum}: ${step.title}${hasNotes ? " (has notes)" : ""}`}
              style={{
                width: "28px",
                height: "28px",
                borderRadius: "9999px",
                border: `2px solid ${isStarted ? step.color : "#E2E8F0"}`,
                background: isStarted ? step.lightColor : "#FFFFFF",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 150ms ease-out",
                fontSize: "10px",
                fontWeight: 600,
                color: isStarted ? step.color : "#94A3B8",
                padding: 0,
                lineHeight: 1,
              }}
            >
              {isStarted ? <i className="bi bi-check" style={{ fontSize: "11px" }}></i> : stepNum}
            </button>
            {hasNotes && (
              <span
                style={{
                  position: "absolute",
                  top: "-2px",
                  right: "-2px",
                  width: "9px",
                  height: "9px",
                  borderRadius: "9999px",
                  background: "#F59E0B",
                  border: "1.5px solid #FFFFFF",
                  display: "block",
                  pointerEvents: "none",
                }}
              />
            )}
          </div>
        );
      })}
    </div>
    <div
      style={{
        display: "flex",
        gap: "16px",
        marginTop: "10px",
        flexWrap: "wrap",
      }}
    >
      {[
        { label: "Market Opportunity",    color: "#3B82F6" },
        { label: "Customer Understanding", color: "#8B5CF6" },
        { label: "Value & Product",        color: "#EC4899" },
        { label: "Business Model",         color: "#F59E0B" },
        { label: "Validation & Execution", color: "#10B981" },
      ].map(({ label, color }) => (
        <span
          key={label}
          style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "11px", color: "#475569" }}
        >
          <span
            style={{
              width: "8px",
              height: "8px",
              borderRadius: "9999px",
              background: color,
              flexShrink: 0,
            }}
          />
          {label}
        </span>
      ))}
    </div>
  </div>
);

// ─── Relative timestamp helper ────────────────────────────────────────────────

function timeAgo(isoString: string): string {
  const now = Date.now();
  const then = new Date(isoString).getTime();
  if (isNaN(then)) return "recently";
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return "yesterday";
  if (diffDays < 30) return `${diffDays}d ago`;
  const diffMonths = Math.floor(diffDays / 30);
  return `${diffMonths}mo ago`;
}

// ─── ScoreBreakdownPanel component ───────────────────────────────────────────

const CATEGORY_META: {
  key: keyof CategoryScores;
  label: string;
  color: string;
  lightColor: string;
  steps: number[]; // 1-indexed step numbers in this category
}[] = [
  { key: "marketOpportunity",    label: "Market Opportunity",    color: "#3B82F6", lightColor: "#DBEAFE", steps: [1,2,3,4,5] },
  { key: "customerUnderstanding",label: "Customer Understanding",color: "#8B5CF6", lightColor: "#EDE9FE", steps: [6,7,8,9,10] },
  { key: "valueAndProduct",      label: "Value & Product",       color: "#EC4899", lightColor: "#FCE7F3", steps: [11,12,13,14] },
  { key: "businessModel",        label: "Business Model",        color: "#F59E0B", lightColor: "#FEF3C7", steps: [15,16,17,18,19] },
  { key: "validationExecution",  label: "Validation & Execution",color: "#10B981", lightColor: "#D1FAE5", steps: [20,21,22,23,24] },
];

function scoreColor(score: number): string {
  if (score >= 70) return "#10B981";
  if (score >= 40) return "#F59E0B";
  return "#EF4444";
}

function scoreBackground(score: number): string {
  if (score >= 70) return "#D1FAE5";
  if (score >= 40) return "#FEF3C7";
  return "#FEE2E2";
}

interface ScoreBreakdownPanelProps {
  categoryScores: CategoryScores;
  completionMap: Record<string, number>;
  onStepClick: (stepNum: number) => void;
  isTeamProject?: boolean;
  stepAssignments?: Record<string, StepAssignmentInfo>;
}

// Map STEP_CONFIGS defaultRole per step number for ScoreBreakdownPanel
const STEP_DEFAULT_ROLE = STEP_CONFIGS.reduce<Record<number, "strategist" | "executor">>(
  (acc, s) => { acc[s.stepNumber] = s.defaultRole; return acc; },
  {}
);

const ScoreBreakdownPanel: React.FC<ScoreBreakdownPanelProps> = ({
  categoryScores,
  completionMap,
  onStepClick,
  isTeamProject = false,
  stepAssignments = {},
}) => {
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);

  return (
    <div
      style={{
        background: "#FFFFFF",
        border: "1px solid #E2E8F0",
        borderRadius: "12px",
        padding: "16px 20px",
        marginBottom: "16px",
        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
      }}
    >
      {/* Header */}
      <div
        style={{
          fontSize: "13px",
          fontWeight: 600,
          color: "#475569",
          marginBottom: "14px",
          display: "flex",
          alignItems: "center",
          gap: "6px",
        }}
      >
        <i className="bi bi-bar-chart-line" style={{ fontSize: "14px" }}></i>
        Score Breakdown
      </div>

      {/* Category rows */}
      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        {CATEGORY_META.map(({ key, label, color, lightColor, steps }) => {
          const score = Math.round(categoryScores[key] ?? 0);
          const fgColor = scoreColor(score);
          const bgColor = scoreBackground(score);

          const isWeak = score < 70;

          // Quick wins: up to 3 incomplete steps in this category (for score < 40)
          const quickWins = steps
            .filter((n) => (completionMap[`step${n}`] ?? 0) === 0)
            .slice(0, 3)
            .map((n) => ({ stepNum: n, title: MINI_MAP_STEP_DATA[n - 1]?.title ?? `Step ${n}` }));

          // Team role split: how many strategist vs executor steps in this category
          const catStrategistSteps = steps.filter((n) => STEP_DEFAULT_ROLE[n] === "strategist");
          const catExecutorSteps   = steps.filter((n) => STEP_DEFAULT_ROLE[n] === "executor");
          const isExpanded = isTeamProject && expandedCategory === key;

          return (
            <div
              key={key}
              style={{
                padding: "12px 16px",
                borderRadius: "8px",
                background: isWeak && score < 40 ? "#FFF7F7" : "transparent",
                transition: "background 150ms ease-out",
              }}
            >
              {/* Main row */}
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                {/* Colour dot */}
                <span
                  style={{
                    width: "10px",
                    height: "10px",
                    borderRadius: "9999px",
                    background: color,
                    flexShrink: 0,
                  }}
                />

                {/* Label */}
                <span
                  style={{
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "#0F172A",
                    minWidth: "160px",
                    flexShrink: 0,
                  }}
                >
                  {label}
                </span>

                {/* Progress bar */}
                <div
                  style={{
                    flex: 1,
                    height: "6px",
                    background: "#F1F5F9",
                    borderRadius: "9999px",
                    overflow: "hidden",
                    minWidth: "60px",
                  }}
                >
                  <div
                    style={{
                      width: `${Math.min(score, 100)}%`,
                      height: "100%",
                      background: fgColor,
                      borderRadius: "9999px",
                      transition: "width 600ms cubic-bezier(0.33,1,0.68,1)",
                    }}
                  />
                </div>

                {/* Score badge */}
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: 700,
                    color: fgColor,
                    background: bgColor,
                    borderRadius: "4px",
                    padding: "2px 8px",
                    flexShrink: 0,
                    minWidth: "44px",
                    textAlign: "center",
                  }}
                >
                  {score}%
                </span>

                {/* Team role split chip + expand toggle */}
                {isTeamProject && (
                  <button
                    type="button"
                    onClick={() => setExpandedCategory(isExpanded ? null : (key as string))}
                    title={isExpanded ? "Hide step breakdown" : "Show step breakdown by role"}
                    style={{
                      display:       "flex",
                      alignItems:    "center",
                      gap:           "4px",
                      background:    "transparent",
                      border:        "none",
                      cursor:        "pointer",
                      padding:       "2px 4px",
                      borderRadius:  "4px",
                      flexShrink:    0,
                    }}
                  >
                    {catStrategistSteps.length > 0 && (
                      <span
                        style={{
                          fontSize:    "10px",
                          fontWeight:  600,
                          color:       "#1D4ED8",
                          background:  "#DBEAFE",
                          borderRadius:"3px",
                          padding:     "1px 5px",
                          whiteSpace:  "nowrap",
                        }}
                      >
                        S{catStrategistSteps.length}
                      </span>
                    )}
                    {catExecutorSteps.length > 0 && (
                      <span
                        style={{
                          fontSize:    "10px",
                          fontWeight:  600,
                          color:       "#065F46",
                          background:  "#D1FAE5",
                          borderRadius:"3px",
                          padding:     "1px 5px",
                          whiteSpace:  "nowrap",
                        }}
                      >
                        E{catExecutorSteps.length}
                      </span>
                    )}
                    <i
                      className={`bi bi-chevron-${isExpanded ? "up" : "down"}`}
                      style={{ fontSize: "10px", color: "#94A3B8" }}
                    />
                  </button>
                )}
              </div>

              {/* Team step-level breakdown — expanded */}
              {isTeamProject && isExpanded && (
                <div
                  style={{
                    marginTop:    "8px",
                    paddingLeft:  "20px",
                    display:      "flex",
                    flexDirection:"column",
                    gap:          "4px",
                  }}
                >
                  {steps.map((stepNum) => {
                    const stepKey     = `step${stepNum}`;
                    const done        = (completionMap[stepKey] ?? 0) > 0;
                    const role        = STEP_DEFAULT_ROLE[stepNum];
                    const title       = MINI_MAP_STEP_DATA[stepNum - 1]?.title ?? `Step ${stepNum}`;
                    const assignment  = stepAssignments[stepKey];
                    const assignedTo  = assignment?.assignedToEmail ?? null;
                    const roleColor   = role === "strategist" ? "#1D4ED8" : "#065F46";
                    const roleBg      = role === "strategist" ? "#DBEAFE" : "#D1FAE5";
                    const roleLabel   = role === "strategist" ? "S" : "E";
                    return (
                      <button
                        key={stepKey}
                        type="button"
                        onClick={() => onStepClick(stepNum)}
                        style={{
                          display:     "flex",
                          alignItems:  "center",
                          gap:         "8px",
                          background:  "transparent",
                          border:      "none",
                          borderRadius:"6px",
                          padding:     "4px 6px",
                          cursor:      "pointer",
                          textAlign:   "left",
                          width:       "100%",
                          transition:  "background 150ms ease-out",
                        }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "#F8FAFC"; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "transparent"; }}
                      >
                        {/* Completion indicator */}
                        <span
                          style={{
                            width:        "14px",
                            height:       "14px",
                            borderRadius: "9999px",
                            border:       `2px solid ${done ? "#10B981" : "#E2E8F0"}`,
                            background:   done ? "#D1FAE5" : "#FFFFFF",
                            flexShrink:   0,
                            display:      "flex",
                            alignItems:   "center",
                            justifyContent:"center",
                          }}
                        >
                          {done && <i className="bi bi-check" style={{ fontSize: "9px", color: "#10B981" }} />}
                        </span>
                        {/* Step number */}
                        <span style={{ fontSize: "10px", color: "#94A3B8", fontWeight: 500, flexShrink: 0, minWidth: "18px" }}>
                          {String(stepNum).padStart(2, "0")}
                        </span>
                        {/* Title */}
                        <span
                          style={{
                            fontSize:     "11px",
                            color:        done ? "#475569" : "#0F172A",
                            flex:         1,
                            overflow:     "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace:   "nowrap",
                            fontWeight:   done ? 400 : 500,
                            textDecoration: done ? "line-through" : "none",
                          }}
                        >
                          {title}
                        </span>
                        {/* Role badge */}
                        <span
                          style={{
                            fontSize:     "9px",
                            fontWeight:   700,
                            color:        roleColor,
                            background:   roleBg,
                            borderRadius: "3px",
                            padding:      "1px 4px",
                            flexShrink:   0,
                          }}
                        >
                          {roleLabel}
                        </span>
                        {/* Assigned member */}
                        <span
                          style={{
                            fontSize:    "10px",
                            color:       "#94A3B8",
                            flexShrink:  0,
                            maxWidth:    "90px",
                            overflow:    "hidden",
                            textOverflow:"ellipsis",
                            whiteSpace:  "nowrap",
                          }}
                        >
                          {assignedTo ? assignedTo.split("@")[0] : "—"}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Quick Wins — only when score < 40 */}
              {score < 40 && (
                <div style={{ paddingLeft: "20px", marginTop: "8px" }}>
                  {quickWins.length > 0 ? (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                      {quickWins.map(({ stepNum, title }) => (
                        <button
                          key={stepNum}
                          type="button"
                          onClick={() => onStepClick(stepNum)}
                          title={`Go to Step ${stepNum}: ${title}`}
                          style={{
                            background: lightColor,
                            color: color,
                            border: "none",
                            borderRadius: "9999px",
                            fontSize: "11px",
                            fontWeight: 500,
                            padding: "4px 8px",
                            cursor: "pointer",
                            whiteSpace: "nowrap",
                            transition: "opacity 150ms ease-out",
                          }}
                        >
                          {stepNum}. {title}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <span style={{ fontSize: "11px", color: "#94A3B8" }}>
                      All steps filled — add more detail to each step
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div
        style={{
          display: "flex",
          gap: "14px",
          marginTop: "12px",
          paddingTop: "10px",
          borderTop: "1px solid #F1F5F9",
          flexWrap: "wrap",
        }}
      >
        {[
          { label: "Strong (≥70%)", color: "#10B981" },
          { label: "Needs work (40–69%)", color: "#F59E0B" },
          { label: "Weak (<40%)", color: "#EF4444" },
        ].map(({ label, color }) => (
          <span
            key={label}
            style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "11px", color: "#475569" }}
          >
            <span
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "9999px",
                background: color,
                flexShrink: 0,
              }}
            />
            {label}
          </span>
        ))}
      </div>
    </div>
  );
};

// ─── RecentActivity component ─────────────────────────────────────────────────

interface RecentActivityProps {
  activityMap: Record<string, string>;
  onStepClick: (stepNum: number) => void;
}

const RecentActivity: React.FC<RecentActivityProps> = ({ activityMap, onStepClick }) => {
  const entries = Object.entries(activityMap)
    .map(([key, updatedAt]) => {
      const stepNum = parseInt(key.replace("step", ""), 10);
      if (isNaN(stepNum) || stepNum < 1 || stepNum > 24) return null;
      const stepData = MINI_MAP_STEP_DATA[stepNum - 1];
      if (!stepData) return null;
      return { stepNum, title: stepData.title, color: stepData.color, lightColor: stepData.lightColor, updatedAt };
    })
    .filter((e): e is NonNullable<typeof e> => e !== null)
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    .slice(0, 5);

  if (entries.length === 0) return null;

  return (
    <div
      style={{
        background: "#FFFFFF",
        border: "1px solid #E2E8F0",
        borderRadius: "12px",
        padding: "16px 20px",
        marginBottom: "16px",
        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
      }}
    >
      <div
        style={{
          fontSize: "13px",
          fontWeight: 600,
          color: "#475569",
          marginBottom: "12px",
          display: "flex",
          alignItems: "center",
          gap: "6px",
        }}
      >
        <i className="bi bi-clock-history" style={{ fontSize: "14px" }}></i>
        Recent Activity
      </div>
      <div className="d-flex flex-column" style={{ gap: "6px" }}>
        {entries.map(({ stepNum, title, color, lightColor, updatedAt }) => (
          <button
            key={`step${stepNum}`}
            type="button"
            onClick={() => onStepClick(stepNum)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              background: "transparent",
              border: "none",
              padding: "6px 8px",
              borderRadius: "8px",
              cursor: "pointer",
              textAlign: "left",
              width: "100%",
              transition: "background 150ms ease-out",
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = lightColor; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
          >
            <span
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "9999px",
                background: color,
                flexShrink: 0,
              }}
            />
            <span
              style={{
                fontSize: "13px",
                fontWeight: 500,
                color: "#0F172A",
                flexGrow: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              <span style={{ color: "#94A3B8", marginRight: "6px" }}>
                {String(stepNum).padStart(2, "0")}
              </span>
              {title}
            </span>
            <span style={{ fontSize: "11px", color: "#94A3B8", whiteSpace: "nowrap", flexShrink: 0 }}>
              {timeAgo(updatedAt)}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

// ─── Score Trend Chart ────────────────────────────────────────────────────────

interface ScoreSnapshot {
  overallScore: number;
  timestamp: string;
}

const ScoreTrendChart: React.FC<{ history: ScoreSnapshot[] }> = ({ history }) => {
  if (history.length < 2) return null;

  const W = 220;
  const H = 52;
  const PAD = 6;

  const scores = history.map((h) => h.overallScore);
  const minScore = Math.max(0, Math.min(...scores) - 10);
  const maxScore = Math.min(100, Math.max(...scores) + 10);
  const range = maxScore - minScore || 1;

  const points = scores.map((score, i) => ({
    x: PAD + (i / (scores.length - 1)) * (W - PAD * 2),
    y: PAD + ((maxScore - score) / range) * (H - PAD * 2),
    score,
  }));

  const polylinePoints = points.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
  const latest = points[points.length - 1] ?? { x: 0, y: 0, score: 0 };
  const latestScore = scores[scores.length - 1] ?? 0;
  const delta = Math.round(latestScore - (scores[0] ?? 0));
  const lineColor = delta >= 0 ? "#10B981" : "#EF4444";

  return (
    <div
      style={{
        background: "#FFFFFF",
        border: "1px solid #E2E8F0",
        borderRadius: "12px",
        padding: "16px 20px",
        marginBottom: "16px",
        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
      }}
    >
      <div
        style={{
          fontSize: "13px",
          fontWeight: 600,
          color: "#475569",
          marginBottom: "14px",
          display: "flex",
          alignItems: "center",
          gap: "6px",
        }}
      >
        <i className="bi bi-graph-up" style={{ fontSize: "14px" }}></i>
        Score Trend
        <span
          style={{
            marginLeft: "auto",
            fontSize: "11px",
            fontWeight: 500,
            color: "#94A3B8",
          }}
        >
          {history.length} data {history.length === 1 ? "point" : "points"}
        </span>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
        {/* Sparkline */}
        <svg
          width={W}
          height={H}
          style={{ overflow: "visible", flexShrink: 0 }}
          aria-label={`Score trend chart: ${latestScore}%`}
        >
          <polyline
            fill="none"
            stroke={lineColor}
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
            points={polylinePoints}
          />
          {/* Endpoint dot */}
          <circle cx={latest.x} cy={latest.y} r={4} fill={lineColor} />
        </svg>

        {/* Score + delta */}
        <div style={{ display: "flex", flexDirection: "column", gap: "3px", flexShrink: 0 }}>
          <span style={{ fontSize: "22px", fontWeight: 700, color: "#0F172A", lineHeight: 1 }}>
            {latestScore}%
          </span>
          {delta !== 0 && (
            <span
              style={{
                fontSize: "11px",
                fontWeight: 600,
                color: lineColor,
              }}
            >
              {delta > 0 ? "▲" : "▼"} {Math.abs(delta)} pts vs first
            </span>
          )}
          {delta === 0 && (
            <span style={{ fontSize: "11px", color: "#94A3B8" }}>No change</span>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── ActivityCalendar component ──────────────────────────────────────────────

function buildActivityData(activityMap: Record<string, string>): Map<string, number> {
  // activityMap is stepKey → ISO updatedAt string; multiple steps can share a day
  const result = new Map<string, number>();
  Object.values(activityMap).forEach((isoDate) => {
    if (!isoDate) return;
    const dateKey = isoDate.substring(0, 10); // YYYY-MM-DD
    result.set(dateKey, (result.get(dateKey) ?? 0) + 1);
  });
  return result;
}

function calculateStreak(activityMap: Record<string, string>): number {
  // Build set of active days (YYYY-MM-DD) from the activityMap (no extra reads)
  const activeDays = new Set<string>();
  Object.values(activityMap).forEach((isoDate) => {
    if (!isoDate) return;
    activeDays.add(isoDate.substring(0, 10));
  });
  // Walk backwards from yesterday — streak breaks if any day has no activity
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  let streak = 0;
  const cursor = new Date(today);
  cursor.setDate(cursor.getDate() - 1); // start from yesterday
  while (true) {
    const key = cursor.toISOString().substring(0, 10);
    if (!activeDays.has(key)) break;
    streak++;
    cursor.setDate(cursor.getDate() - 1);
  }
  return streak;
}

const ActivityCalendar: React.FC<{ activityMap: Record<string, string> }> = ({ activityMap }) => {
  const activityData = buildActivityData(activityMap);
  if (activityData.size === 0) return null;

  // Build a 13-week (91-day) grid ending today
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const DAYS = 91;
  // Start from DAYS ago, aligned to Sunday
  const startDate = new Date(today);
  startDate.setDate(startDate.getDate() - (DAYS - 1));

  // Build array of 91 date objects
  const cells: { date: Date; key: string; count: number }[] = [];
  for (let i = 0; i < DAYS; i++) {
    const d = new Date(startDate);
    d.setDate(d.getDate() + i);
    const key = d.toISOString().substring(0, 10);
    cells.push({ date: d, key, count: activityData.get(key) ?? 0 });
  }

  // Group by week (7 days each)
  const weeks: typeof cells[] = [];
  for (let w = 0; w < 13; w++) {
    weeks.push(cells.slice(w * 7, w * 7 + 7));
  }

  const cellColor = (count: number): string => {
    if (count === 0) return "#F1F5F9";
    if (count === 1) return "#D1FAE5";
    if (count <= 3) return "#6EE7B7";
    return "#10B981";
  };

  const monthLabels: { label: string; weekIdx: number }[] = [];
  weeks.forEach((week, wi) => {
    const firstDay = week[0]?.date;
    if (!firstDay) return;
    if (firstDay.getDate() <= 7) {
      monthLabels.push({
        label: firstDay.toLocaleString("default", { month: "short" }),
        weekIdx: wi,
      });
    }
  });

  const CELL = 12;
  const GAP = 3;

  return (
    <div
      style={{
        background: "#FFFFFF",
        border: "1px solid #E2E8F0",
        borderRadius: "12px",
        padding: "16px 20px",
        marginBottom: "16px",
        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
        overflowX: "auto",
      }}
    >
      <div
        style={{
          fontSize: "13px",
          fontWeight: 600,
          color: "#475569",
          marginBottom: "12px",
          display: "flex",
          alignItems: "center",
          gap: "6px",
        }}
      >
        <i className="bi bi-calendar3" style={{ fontSize: "14px" }}></i>
        Activity — Last 90 Days
        <span
          style={{ marginLeft: "auto", fontSize: "11px", fontWeight: 500, color: "#94A3B8" }}
        >
          {activityData.size} active {activityData.size === 1 ? "day" : "days"}
        </span>
      </div>

      <div style={{ display: "inline-flex", flexDirection: "column", gap: 0 }}>
        {/* Month labels row */}
        <div style={{ display: "flex", gap: GAP, marginBottom: 4 }}>
          {weeks.map((_, wi) => {
            const label = monthLabels.find((m) => m.weekIdx === wi);
            return (
              <div
                key={wi}
                style={{ width: CELL, fontSize: "9px", color: "#94A3B8", fontWeight: 500, textAlign: "center", lineHeight: 1 }}
              >
                {label ? label.label : ""}
              </div>
            );
          })}
        </div>

        {/* Grid: rows = days of week (0=Sun..6=Sat), cols = weeks */}
        <div style={{ display: "flex", gap: GAP }}>
          {weeks.map((week, wi) => (
            <div key={wi} style={{ display: "flex", flexDirection: "column", gap: GAP }}>
              {week.map((cell, di) => {
                const dateStr = cell.date.toLocaleDateString("default", {
                  weekday: "short", month: "short", day: "numeric",
                });
                const title = cell.count > 0
                  ? `${cell.count} step save${cell.count !== 1 ? "s" : ""} on ${dateStr}`
                  : `No activity on ${dateStr}`;
                return (
                  <div
                    key={di}
                    title={title}
                    style={{
                      width: CELL,
                      height: CELL,
                      borderRadius: "3px",
                      background: cellColor(cell.count),
                      cursor: cell.count > 0 ? "default" : "default",
                      flexShrink: 0,
                    }}
                  />
                );
              })}
            </div>
          ))}
        </div>

        {/* Legend */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            marginTop: "10px",
            fontSize: "10px",
            color: "#94A3B8",
          }}
        >
          <span>Less</span>
          {["#F1F5F9", "#D1FAE5", "#6EE7B7", "#10B981"].map((c) => (
            <div
              key={c}
              style={{ width: CELL, height: CELL, borderRadius: "3px", background: c }}
            />
          ))}
          <span>More</span>
        </div>
      </div>
    </div>
  );
};

// ─── Props / main component ───────────────────────────────────────────────────

interface Props {
  userID: string;
  projectTitle: string;
  projectID: string;
  projectDescription: string;
}

export const ProjectDashboardPage: React.FC<Props> = ({ userID, projectTitle, projectID, projectDescription }) => {
  const navigate = useNavigate();
  const [showCreateAppModal, setShowCreateAppModal] = useState(false);
  const [showLogoModal, setShowLogoModal] = useState(false);
  const [showWebsiteModal, setShowWebsiteModal] = useState(false);
  const [showSurveyModal, setShowSurveyModal] = useState(false);
  const [showMarketingModal, setShowMarketingModal] = useState(false);
  const [showMVPModal, setShowMVPModal] = useState(false);
  const [showSEOModal, setShowSEOModal] = useState(false);
  const [showContentModal, setShowContentModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showSocialModal, setShowSocialModal] = useState(false);
  const [showPersonaTestModal, setShowPersonaTestModal] = useState(false);
  const [shareSlug, setShareSlug] = useState<string | null>(null);
  const [shareCopied, setShareCopied] = useState(false);
  const [progressData, setProgressData] = useState({ stepsStarted: 0, projectProgress: 0 });
  const [stepCompletionMap, setStepCompletionMap] = useState<Record<string, number>>({});
  const [stepNotesMap, setStepNotesMap] = useState<Record<string, boolean>>({});
  const [stepActivityMap, setStepActivityMap] = useState<Record<string, string>>({}); // stepKey → ISO updatedAt
  const [stepAssignments, setStepAssignments] = useState<Record<string, StepAssignmentInfo>>({}); // stepKey → assignment
  const [projectStatus, setProjectStatus] = useState<string | undefined>();
  const [categoryScores, setCategoryScores] = useState<CategoryScores | undefined>();
  const [categoryReasons, setCategoryReasons] = useState<CategoryReasons | undefined>();
  const [scoreHistory, setScoreHistory] = useState<ScoreSnapshot[]>([]);
  // Feedback state
  const [feedbackDraft, setFeedbackDraft] = useState({ whatWorking: "", whatToImprove: "", keyMetric: "" });
  const [isRevalidating, setIsRevalidating] = useState(false);
  const { calculateScore } = useBusinessAI();
  const { generateLogo, isGenerating: isGeneratingLogo, error: logoError } = useLogoGenerator();
  const { exportPDF, isExporting, error: pdfError } = usePDFExport();
  const { exportBuildPackage, exportZipPackage, exportNotesMarkdown, isExporting: isBuildExporting, isZipping: isBuildZipping, isExportingNotes, error: buildExportError } = useValidationExport();
  const { buildStatus, buildUrl, setBuildQueued, isPolling } = useBuildStatus(userID, projectID);
  const { feedback, isSaving: isSavingFeedback, error: feedbackError, loadFeedback, saveFeedback } = useFeedbackCapture();
  const { canUseFeature, isAgency } = useSubscription();
  const { generateReport, isGenerating: isGeneratingReport } = usePublicReport();
  const { setBusinessScore } = usePageData();

  const canExportPDF = canUseFeature("pdf_export");

  // Team + share state
  const [team, setTeam] = useState<Team | null>(null);
  const [teamLoading, setTeamLoading] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [projectCollaborators, setProjectCollaborators] = useState<Collaborator[]>([]);

  // Load team once when userID is available and user is Agency tier
  useEffect(() => {
    if (!userID || !isAgency) return;
    setTeamLoading(true);
    const teamsRef = collection(db, "teams");
    const q = query(teamsRef, where("createdBy", "==", userID));
    getDocs(q)
      .then((snap) => {
        if (!snap.empty && snap.docs[0]) {
          const d = snap.docs[0];
          setTeam({ id: d.id, ...(d.data() as Omit<Team, "id">) });
        } else {
          // Also check teams where user is a member
          const q2 = query(teamsRef, where("memberUIDs", "array-contains", userID));
          return getDocs(q2).then((snap2) => {
            if (!snap2.empty && snap2.docs[0]) {
              const d = snap2.docs[0];
              setTeam({ id: d.id, ...(d.data() as Omit<Team, "id">) });
            }
          });
        }
      })
      .catch(() => {/* non-critical */})
      .finally(() => setTeamLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userID, isAgency]);

  // Load current project collaborators whenever projectID changes
  useEffect(() => {
    if (!userID || !projectID) return;
    getDoc(doc(db, "users", userID, "projects", projectID))
      .then((snap) => {
        const data = snap.data();
        const collabs: Collaborator[] = Array.isArray(data?.collaborators) ? data.collaborators : [];
        setProjectCollaborators(collabs);
      })
      .catch(() => {/* non-critical */});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userID, projectID]);


  // Save logo to Firestore
  const saveLogo = async (logoUrl: string) => {
    if (!userID || !projectID) return;

    try {
      const projectRef = doc(db, "users", userID, "projects", projectID);
      await updateDoc(projectRef, {
        logoUrl,
        logoUpdatedAt: new Date().toISOString(),
      });
    } catch (error) {
      console.error("Failed to save logo:", error);
    }
  };

  // Fetch project progress
  const fetchProgress = useCallback(async () => {
    if (!userID || !projectID) return;

    const userRef = doc(db, "users", userID);
    const thisProjectRef = doc(userRef, "projects", projectID);
    const thisProjectSnapshot = await getDoc(thisProjectRef);
    const stepsStartedSnapshot = await getDocs(collection(thisProjectRef, "progress"));

    const projectData = thisProjectSnapshot.data();

    // Build per-step completion map, notes map, activity map, and assignments
    const completionMap: Record<string, number> = {};
    const notesMap: Record<string, boolean> = {};
    const activityMap: Record<string, string> = {};
    const assignmentsMap: Record<string, StepAssignmentInfo> = {};
    stepsStartedSnapshot.docs.forEach((stepDoc) => {
      const data = stepDoc.data();
      const progress = data?.stepProgress ?? 0;
      completionMap[stepDoc.id] = typeof progress === "number" ? progress : 0;
      const notes = data?.notes;
      notesMap[stepDoc.id] = typeof notes === "string" && notes.trim().length > 0;
      if (typeof data?.updatedAt === "string" && data.updatedAt) {
        activityMap[stepDoc.id] = data.updatedAt;
      }
      // Read assignment fields for TeamProgressChart + ScoreBreakdownPanel
      if (data?.assignedTo || data?.assignedRole) {
        assignmentsMap[stepDoc.id] = {
          assignedToEmail: typeof data.assignedToEmail === "string" ? data.assignedToEmail : null,
          assignedRole:    typeof data.assignedRole    === "string" ? data.assignedRole    : null,
        };
      }
    });
    setStepCompletionMap(completionMap);
    setStepNotesMap(notesMap);
    setStepActivityMap(activityMap);
    setStepAssignments(assignmentsMap);

    setProgressData({
      stepsStarted: stepsStartedSnapshot.docs.length,
      projectProgress: projectData?.projectProgress || 0
    });
    if (typeof projectData?.projectStatus === "string") {
      setProjectStatus(projectData.projectStatus);
    }
  }, [userID, projectID]);

  // Save a score snapshot to scoreHistory sub-collection
  const saveScoreSnapshot = useCallback(async (overallScore: number) => {
    if (!userID || !projectID) return;
    try {
      await addDoc(
        collection(db, "users", userID, "projects", projectID, "scoreHistory"),
        { overallScore, timestamp: new Date().toISOString() }
      );
    } catch (err) {
      console.error("Failed to save score snapshot:", err);
    }
  }, [userID, projectID]);

  // Fetch last 30 score snapshots, deduplicate to 1/day, set scoreHistory state
  const fetchScoreHistory = useCallback(async () => {
    if (!userID || !projectID) return;
    try {
      const histRef = collection(db, "users", userID, "projects", projectID, "scoreHistory");
      const q = query(histRef, orderBy("timestamp", "desc"), limit(30));
      const snap = await getDocs(q);
      if (snap.empty) { setScoreHistory([]); return; }

      // Deduplicate: one entry per calendar day (keep latest for the day)
      const byDate = new Map<string, ScoreSnapshot>();
      snap.docs.forEach((d) => {
        const data = d.data();
        const ts: string = typeof data.timestamp === "string" ? data.timestamp : "";
        const dateKey = ts.substring(0, 10); // YYYY-MM-DD
        const existing = byDate.get(dateKey);
        if (!existing || ts > existing.timestamp) {
          byDate.set(dateKey, { overallScore: data.overallScore ?? 0, timestamp: ts });
        }
      });

      const sorted = Array.from(byDate.values())
        .sort((a, b) => a.timestamp.localeCompare(b.timestamp))
        .slice(-10);

      setScoreHistory(sorted);
    } catch (err) {
      console.error("Failed to fetch score history:", err);
    }
  }, [userID, projectID]);

  // Fetch category scores and reasons for StepsList
  const fetchCategoryData = useCallback(async () => {
    if (!projectID) return;
    const result = await calculateScore(projectID);
    if (result) {
      setCategoryScores(result.categoryScores);
      setCategoryReasons(result.categoryReasons);
      setBusinessScore(result.overallScore);
      await saveScoreSnapshot(result.overallScore);
    }
  }, [projectID, calculateScore, setBusinessScore, saveScoreSnapshot]);

  useEffect(() => {
    fetchProgress();
    fetchCategoryData();
    fetchScoreHistory();
  }, [fetchProgress, fetchCategoryData, fetchScoreHistory]);

  // Load feedback when build is complete
  useEffect(() => {
    if (buildStatus === "complete") {
      loadFeedback(userID, projectID);
    }
  }, [buildStatus, userID, projectID, loadFeedback]);

  const handleShareScore = async () => {
    if (!categoryScores) return;
    const slug = await generateReport({
      projectTitle,
      overallScore: Object.values(categoryScores).reduce((a, b) => a + b, 0) / 5,
      categoryScores,
      categoryReasons: categoryReasons ?? {},
    });
    if (slug) {
      setShareSlug(slug);
      const url = `${window.location.origin}${process.env.PUBLIC_URL}/report/${slug}`;
      try {
        await navigator.clipboard.writeText(url);
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 3000);
      } catch {
        // clipboard unavailable — user can copy from the link shown in the UI
      }
    }
  };

  const isValidationComplete = progressData.stepsStarted >= 24;

  // A "team project" is one where the user is Agency tier, has a team, and has shared it with collaborators
  const isTeamProject = isAgency && team !== null && projectCollaborators.length > 0;

  const handleRevalidate = async () => {
    setIsRevalidating(true);
    try {
      const result = await calculateScore(projectID);
      if (result) {
        setCategoryScores(result.categoryScores);
        setCategoryReasons(result.categoryReasons);
        setBusinessScore(result.overallScore);
        await saveScoreSnapshot(result.overallScore);
        await fetchScoreHistory();
      }
    } catch (err) {
      console.error("Re-validate error:", err);
    } finally {
      setIsRevalidating(false);
    }
  };

  const handleFeedbackSubmit = async () => {
    const { whatWorking, whatToImprove, keyMetric } = feedbackDraft;
    await saveFeedback(userID, projectID, { whatWorking, whatToImprove, keyMetric });
  };

  const STATUS_OPTIONS_DASH = [
    { key: "Ideating",   color: "#475569", bg: "#F1F5F9" },
    { key: "Validating", color: "#3B82F6", bg: "#DBEAFE" },
    { key: "Building",   color: "#8B5CF6", bg: "#EDE9FE" },
    { key: "Launched",   color: "#10B981", bg: "#D1FAE5" },
  ] as const;

  const [showStatusDropdown, setShowStatusDropdown] = useState(false);

  const handleDashStatusChange = async (status: string) => {
    setShowStatusDropdown(false);
    setProjectStatus(status);
    try {
      const projectRef = doc(db, "users", userID, "projects", projectID);
      await updateDoc(projectRef, { projectStatus: status });
    } catch {
      // Non-critical
    }
  };

  const currentStatusStyle = STATUS_OPTIONS_DASH.find((s) => s.key === projectStatus);

  // Close status dropdown on outside click
  useEffect(() => {
    if (!showStatusDropdown) return;
    const handler = () => setShowStatusDropdown(false);
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [showStatusDropdown]);

  // Check for any weak category (< 40%) when scores are available
  const hasWeakCategory =
    categoryScores &&
    Object.values(categoryScores).some((score) => score < 40);

  // Onboarding checklist — show when early-stage (< 3 steps started), hide when dismissed
  const [checklistDismissed, setChecklistDismissed] = useState(false);
  const showChecklist =
    !checklistDismissed &&
    progressData.stepsStarted < 3 &&
    !isValidationComplete;

  const checkStep1Done = (stepCompletionMap["step1"] ?? 0) > 0;
  const checkAutoFillDone = progressData.stepsStarted > 5;
  const checkScoreDone = categoryScores != null;
  const allChecklistDone = checkStep1Done && checkAutoFillDone && checkScoreDone;

  return (
    <>
      {/* begin::Quick-Start Checklist */}
      {showChecklist && !allChecklistDone && (
        <div
          className="card mb-5"
          style={{
            background: "#FFFFFF",
            border: "1px solid #E2E8F0",
            borderRadius: "12px",
            boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
          }}
        >
          <div className="card-body py-4 px-5">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div className="d-flex align-items-center gap-2">
                <i className="bi bi-rocket-takeoff" style={{ color: "#3B82F6", fontSize: "16px" }} />
                <span style={{ fontSize: "14px", fontWeight: 600, color: "#0F172A" }}>
                  Getting Started
                </span>
              </div>
              <button
                type="button"
                onClick={() => setChecklistDismissed(true)}
                style={{
                  background: "none",
                  border: "none",
                  color: "#94A3B8",
                  cursor: "pointer",
                  padding: "2px 6px",
                  fontSize: "16px",
                  lineHeight: 1,
                }}
                title="Dismiss"
              >
                ×
              </button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {/* Item 1 */}
              <div
                className="d-flex align-items-center gap-3"
                style={{ padding: "8px 10px", borderRadius: "8px", background: checkStep1Done ? "#F0FDF4" : "#F8FAFC" }}
              >
                <span
                  style={{
                    width: "20px",
                    height: "20px",
                    borderRadius: "9999px",
                    border: `2px solid ${checkStep1Done ? "#10B981" : "#CBD5E1"}`,
                    background: checkStep1Done ? "#10B981" : "transparent",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {checkStep1Done && <i className="bi bi-check" style={{ color: "white", fontSize: "11px" }} />}
                </span>
                <span style={{ fontSize: "13px", color: checkStep1Done ? "#065F46" : "#0F172A", flex: 1 }}>
                  Fill in Step 1 — Market Segmentation
                </span>
                {!checkStep1Done && (
                  <button
                    type="button"
                    onClick={() =>
                      navigate("/step1", {
                        state: { projectTitle, projectID, projectDescription, userID },
                      })
                    }
                    style={{
                      background: "#3B82F6",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      fontSize: "12px",
                      fontWeight: 600,
                      padding: "4px 10px",
                      cursor: "pointer",
                      flexShrink: 0,
                    }}
                  >
                    Go
                  </button>
                )}
              </div>
              {/* Item 2 */}
              <div
                className="d-flex align-items-center gap-3"
                style={{ padding: "8px 10px", borderRadius: "8px", background: checkAutoFillDone ? "#F0FDF4" : "#F8FAFC" }}
              >
                <span
                  style={{
                    width: "20px",
                    height: "20px",
                    borderRadius: "9999px",
                    border: `2px solid ${checkAutoFillDone ? "#10B981" : "#CBD5E1"}`,
                    background: checkAutoFillDone ? "#10B981" : "transparent",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {checkAutoFillDone && <i className="bi bi-check" style={{ color: "white", fontSize: "11px" }} />}
                </span>
                <span style={{ fontSize: "13px", color: checkAutoFillDone ? "#065F46" : "#0F172A", flex: 1 }}>
                  Run AI Auto-fill on the AI Progress page
                </span>
                {!checkAutoFillDone && (
                  <button
                    type="button"
                    onClick={() =>
                      navigate("/ai-progress", {
                        state: { projectTitle, projectID, projectDescription, userID },
                      })
                    }
                    style={{
                      background: "#8B5CF6",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      fontSize: "12px",
                      fontWeight: 600,
                      padding: "4px 10px",
                      cursor: "pointer",
                      flexShrink: 0,
                    }}
                  >
                    Go
                  </button>
                )}
              </div>
              {/* Item 3 */}
              <div
                className="d-flex align-items-center gap-3"
                style={{ padding: "8px 10px", borderRadius: "8px", background: checkScoreDone ? "#F0FDF4" : "#F8FAFC" }}
              >
                <span
                  style={{
                    width: "20px",
                    height: "20px",
                    borderRadius: "9999px",
                    border: `2px solid ${checkScoreDone ? "#10B981" : "#CBD5E1"}`,
                    background: checkScoreDone ? "#10B981" : "transparent",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {checkScoreDone && <i className="bi bi-check" style={{ color: "white", fontSize: "11px" }} />}
                </span>
                <span style={{ fontSize: "13px", color: checkScoreDone ? "#065F46" : "#0F172A", flex: 1 }}>
                  View your Validation Score — calculate AI scores
                </span>
                {!checkScoreDone && (
                  <button
                    type="button"
                    onClick={() =>
                      navigate("/ai-review", {
                        state: { projectTitle, projectID, projectDescription, userID },
                      })
                    }
                    style={{
                      background: "#10B981",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      fontSize: "12px",
                      fontWeight: 600,
                      padding: "4px 10px",
                      cursor: "pointer",
                      flexShrink: 0,
                    }}
                  >
                    Go
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {/* end::Quick-Start Checklist */}

      {/* begin::Validation Complete Banner */}
      {isValidationComplete && (
        <div
          className="card mb-5"
          style={{
            background: "linear-gradient(135deg, #D1FAE5 0%, #A7F3D0 100%)",
            border: "1px solid #10B981",
            borderRadius: "12px",
            boxShadow: "0 4px 6px -1px rgba(16,185,129,0.1)",
          }}
        >
          <div className="card-body py-5 px-6">
            <div className="d-flex align-items-center flex-wrap gap-4">
              {/* Check icon */}
              <div
                style={{
                  width: "52px",
                  height: "52px",
                  background: "#10B981",
                  borderRadius: "9999px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <i
                  className="bi bi-check-circle-fill"
                  style={{ color: "white", fontSize: "24px" }}
                />
              </div>
              {/* Text */}
              <div className="flex-grow-1">
                <h4
                  style={{
                    fontSize: "18px",
                    fontWeight: 600,
                    color: "#065F46",
                    marginBottom: "4px",
                  }}
                >
                  Validation Complete!
                </h4>
                <p
                  style={{
                    fontSize: "13px",
                    color: "#047857",
                    marginBottom: 0,
                  }}
                >
                  You've worked through all 24 steps. Review your scores below
                  and prepare to build your MVP.
                </p>
              </div>
              {/* Button group */}
              <div className="d-flex gap-2 flex-shrink-0 flex-wrap">
                {/* Launch Dashboard link */}
                <button
                  className="btn"
                  onClick={() =>
                    navigate("/launch-dashboard", {
                      state: { projectTitle, projectID, projectDescription, userID },
                    })
                  }
                  style={{
                    background: "#8B5CF6",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    fontWeight: 600,
                    fontSize: "14px",
                    padding: "10px 20px",
                    cursor: "pointer",
                    transition: "all 150ms ease-out",
                  }}
                >
                  <i className="bi bi-rocket-takeoff me-2" />
                  Launch Dashboard
                </button>
                {/* Share My Score — generates public report link */}
                {categoryScores && (
                  <button
                    className="btn"
                    onClick={handleShareScore}
                    disabled={isGeneratingReport}
                    title="Generate a public shareable link to your validation score"
                    style={{
                      background: shareCopied ? "#D1FAE5" : "#FFFFFF",
                      color: shareCopied ? "#065F46" : "#475569",
                      border: `1px solid ${shareCopied ? "#10B981" : "#CBD5E1"}`,
                      borderRadius: "8px",
                      fontWeight: 600,
                      fontSize: "14px",
                      padding: "10px 20px",
                      cursor: isGeneratingReport ? "not-allowed" : "pointer",
                      transition: "all 150ms ease-out",
                    }}
                  >
                    <i className={`bi ${isGeneratingReport ? "bi-hourglass-split" : shareCopied ? "bi-check2" : "bi-share"} me-2`} />
                    {isGeneratingReport ? "Generating…" : shareCopied ? "Link Copied!" : "Share Score"}
                  </button>
                )}
                {/* Export Notes — only shown when at least one step has notes */}
                {Object.values(stepNotesMap).some(Boolean) && (
                  <button
                    className="btn"
                    onClick={async () => { await exportNotesMarkdown(userID, projectID); }}
                    disabled={isExportingNotes}
                    title="Download all your step notes as a Markdown file"
                    style={{
                      background: isExportingNotes ? "#CCFBF1" : "#0D9488",
                      color: isExportingNotes ? "#0F766E" : "white",
                      borderRadius: "8px",
                      fontWeight: 600,
                      fontSize: "14px",
                      padding: "10px 20px",
                      cursor: isExportingNotes ? "not-allowed" : "pointer",
                      transition: "all 150ms ease-out",
                    }}
                  >
                    <i className={`bi ${isExportingNotes ? "bi-hourglass-split" : "bi-journal-text"} me-2`} />
                    {isExportingNotes ? "Exporting…" : "Export Notes"}
                  </button>
                )}
                {/* PDF Export — Pro feature */}
                <button
                  className="btn"
                  onClick={() => exportPDF(userID, projectID)}
                  disabled={isExporting || !canExportPDF}
                  title={
                    !canExportPDF
                      ? "PDF Export requires a Pro subscription"
                      : "Download your validation report as PDF"
                  }
                  style={{
                    background: canExportPDF ? "#3B82F6" : "#F1F5F9",
                    color: canExportPDF ? "white" : "#94A3B8",
                    border: canExportPDF ? "none" : "1px solid #E2E8F0",
                    borderRadius: "8px",
                    fontWeight: 600,
                    fontSize: "14px",
                    padding: "10px 20px",
                    cursor: canExportPDF && !isExporting ? "pointer" : "not-allowed",
                  }}
                >
                  <i
                    className={`bi ${isExporting ? "bi-hourglass-split" : "bi-file-earmark-pdf"} me-2`}
                  />
                  {isExporting ? "Generating…" : "Download PDF"}
                  {!canExportPDF && (
                    <span
                      style={{
                        fontSize: "11px",
                        marginLeft: "6px",
                        opacity: 0.8,
                      }}
                    >
                      (Pro)
                    </span>
                  )}
                </button>
                {/* Download ZIP — bundles all 3 export files into a single ZIP */}
                <button
                  className="btn"
                  onClick={async () => { await exportZipPackage(userID, projectID); }}
                  disabled={isBuildZipping}
                  title="Download VALIDATION.md + PRODUCT-SPEC.md + project-meta.json as a ZIP"
                  style={{
                    background: isBuildZipping ? "#EDE9FE" : "#8B5CF6",
                    color: isBuildZipping ? "#5B21B6" : "white",
                    borderRadius: "8px",
                    fontWeight: 600,
                    fontSize: "14px",
                    padding: "10px 20px",
                    cursor: isBuildZipping ? "not-allowed" : "pointer",
                    transition: "all 150ms ease-out",
                  }}
                >
                  <i className={`bi ${isBuildZipping ? "bi-hourglass-split" : "bi-file-zip"} me-2`} />
                  {isBuildZipping ? "Zipping…" : "Download ZIP"}
                </button>
                {/* Build It — exports VALIDATION.md + PRODUCT-SPEC.md + project-meta.json */}
                <button
                  className="btn"
                  onClick={async () => {
                    await exportBuildPackage(userID, projectID);
                    // Mark project as queued in Firestore so pipeline status can be tracked
                    await setBuildQueued();
                  }}
                  disabled={isBuildExporting || buildStatus === "queued" || buildStatus === "building"}
                  title={
                    buildStatus === "queued" || buildStatus === "building"
                      ? "Build is already in progress"
                      : "Export your validation package for the build pipeline"
                  }
                  style={{
                    background: isBuildExporting || buildStatus === "queued" || buildStatus === "building" ? "#D1FAE5" : "#10B981",
                    color: isBuildExporting || buildStatus === "queued" || buildStatus === "building" ? "#065F46" : "white",
                    borderRadius: "8px",
                    fontWeight: 600,
                    fontSize: "14px",
                    padding: "10px 20px",
                    cursor: isBuildExporting || buildStatus === "queued" || buildStatus === "building" ? "not-allowed" : "pointer",
                    transition: "all 150ms ease-out",
                  }}
                >
                  <i
                    className={`bi ${isBuildExporting ? "bi-hourglass-split" : "bi-rocket-takeoff"} me-2`}
                  />
                  {isBuildExporting ? "Exporting…" : "Build It"}
                </button>
              </div>
            </div>
            {/* Shareable report link */}
            {shareSlug && (
              <div
                className="d-flex align-items-center gap-3 mt-3 p-3"
                style={{
                  background: "#F0FDF4",
                  borderRadius: "8px",
                  border: "1px solid #BBF7D0",
                  flexWrap: "wrap",
                }}
              >
                <i className="bi bi-link-45deg" style={{ color: "#10B981", fontSize: "18px", flexShrink: 0 }} />
                <span style={{ fontSize: "13px", color: "#065F46", flex: 1, minWidth: 0, wordBreak: "break-all" }}>
                  Public link:{" "}
                  <a
                    href={`${window.location.origin}${process.env.PUBLIC_URL}/report/${shareSlug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#047857", fontWeight: 600 }}
                  >
                    {window.location.origin}{process.env.PUBLIC_URL}/report/{shareSlug}
                  </a>
                </span>
                <button
                  className="btn"
                  onClick={async () => {
                    await navigator.clipboard.writeText(`${window.location.origin}${process.env.PUBLIC_URL}/report/${shareSlug}`);
                    setShareCopied(true);
                    setTimeout(() => setShareCopied(false), 3000);
                  }}
                  style={{
                    background: "transparent",
                    color: "#047857",
                    border: "1px solid #10B981",
                    borderRadius: "6px",
                    fontSize: "12px",
                    fontWeight: 600,
                    padding: "5px 12px",
                    cursor: "pointer",
                    flexShrink: 0,
                  }}
                >
                  <i className={`bi ${shareCopied ? "bi-check2" : "bi-clipboard"} me-1`} />
                  {shareCopied ? "Copied!" : "Copy"}
                </button>
              </div>
            )}
            {/* Build export error */}
            {buildExportError && (
              <div
                className="d-flex align-items-center mt-3 p-3"
                style={{
                  background: "rgba(254,226,226,0.8)",
                  borderRadius: "8px",
                  border: "1px solid #FCA5A544",
                }}
              >
                <i
                  className="bi bi-exclamation-circle me-2"
                  style={{ color: "#DC2626", fontSize: "16px", flexShrink: 0 }}
                />
                <span style={{ fontSize: "13px", color: "#991B1B" }}>
                  {buildExportError}
                </span>
              </div>
            )}
            {/* Low-score warning */}
            {hasWeakCategory && (
              <div
                className="d-flex align-items-center mt-4 p-3"
                style={{
                  background: "rgba(254,242,194,0.8)",
                  borderRadius: "8px",
                  border: "1px solid #F59E0B44",
                }}
              >
                <i
                  className="bi bi-exclamation-triangle me-3"
                  style={{ color: "#D97706", fontSize: "18px", flexShrink: 0 }}
                />
                <span style={{ fontSize: "13px", color: "#92400E" }}>
                  Some categories scored below 40%. Consider revisiting weak
                  categories before building — a stronger foundation leads to
                  better outcomes.
                </span>
              </div>
            )}
          </div>
        </div>
      )}
      {/* end::Validation Complete Banner */}

      {/* begin::Build In Progress Banner */}
      {(buildStatus === "queued" || buildStatus === "building") && (
        <div
          className="card mb-5"
          style={{
            background: "linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)",
            border: "1px solid #3B82F6",
            borderRadius: "12px",
            boxShadow: "0 4px 6px -1px rgba(59,130,246,0.1)",
          }}
        >
          <div className="card-body py-4 px-6">
            <div className="d-flex align-items-center gap-4">
              <div
                style={{
                  width: "44px",
                  height: "44px",
                  background: "#3B82F6",
                  borderRadius: "9999px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  animation: "spin 1.5s linear infinite",
                }}
              >
                <i className="bi bi-gear-fill" style={{ color: "white", fontSize: "20px" }} />
              </div>
              <div className="flex-grow-1">
                <h4 style={{ fontSize: "16px", fontWeight: 600, color: "#1E3A5F", marginBottom: "2px" }}>
                  Build in Progress
                </h4>
                <p style={{ fontSize: "13px", color: "#2563EB", marginBottom: 0 }}>
                  {buildStatus === "queued"
                    ? "Your export has been queued. Run the build pipeline on your machine to start building."
                    : "The build pipeline is running. This may take a few minutes."}
                  {isPolling && (
                    <span style={{ marginLeft: "6px", opacity: 0.7 }}>Checking for updates…</span>
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* end::Build In Progress Banner */}

      {/* begin::Build Complete Banner */}
      {buildStatus === "complete" && (
        <div
          className="card mb-5"
          style={{
            background: "linear-gradient(135deg, #F5F3FF 0%, #EDE9FE 100%)",
            border: "1px solid #8B5CF6",
            borderRadius: "12px",
            boxShadow: "0 4px 6px -1px rgba(139,92,246,0.1)",
          }}
        >
          <div className="card-body py-4 px-6">
            <div className="d-flex align-items-center gap-4">
              <div
                style={{
                  width: "44px",
                  height: "44px",
                  background: "#8B5CF6",
                  borderRadius: "9999px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <i className="bi bi-rocket-takeoff-fill" style={{ color: "white", fontSize: "20px" }} />
              </div>
              <div className="flex-grow-1">
                <h4 style={{ fontSize: "16px", fontWeight: 600, color: "#3B0764", marginBottom: "2px" }}>
                  Build Complete!
                </h4>
                <p style={{ fontSize: "13px", color: "#6D28D9", marginBottom: 0 }}>
                  Your MVP has been built by the pipeline.
                </p>
              </div>
              {buildUrl && (
                <a
                  href={buildUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn"
                  style={{
                    background: "#8B5CF6",
                    color: "white",
                    borderRadius: "8px",
                    fontWeight: 600,
                    fontSize: "14px",
                    padding: "10px 20px",
                    textDecoration: "none",
                    flexShrink: 0,
                    transition: "all 150ms ease-out",
                  }}
                >
                  <i className="bi bi-box-arrow-up-right me-2" />
                  View Project
                </a>
              )}
            </div>
          </div>
        </div>
      )}
      {/* end::Build Complete Banner */}

      {/* begin::Feedback Capture Widget */}
      {buildStatus === "complete" && (
        <div
          className="card mb-5"
          style={{
            background: "#FFFFFF",
            border: "1px solid #E2E8F0",
            borderRadius: "12px",
            boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
          }}
        >
          <div className="card-body py-5 px-6">
            {/* Header */}
            <div className="d-flex align-items-center gap-3 mb-4">
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  background: "#EDE9FE",
                  borderRadius: "9999px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <i className="bi bi-chat-square-text" style={{ color: "#8B5CF6", fontSize: "18px" }} />
              </div>
              <div>
                <h4
                  style={{
                    fontSize: "18px",
                    fontWeight: 600,
                    color: "#0F172A",
                    marginBottom: "2px",
                  }}
                >
                  How's the Launch Going?
                </h4>
                <p style={{ fontSize: "13px", color: "#475569", marginBottom: 0 }}>
                  Capture real-world feedback to sharpen your strategy.
                </p>
              </div>
            </div>

            {/* Read-only summary (after submit) */}
            {feedback ? (
              <div>
                <div className="row g-3 mb-4">
                  <div className="col-md-4">
                    <div
                      style={{
                        background: "#F0FDF4",
                        border: "1px solid #BBF7D0",
                        borderRadius: "8px",
                        padding: "14px 16px",
                      }}
                    >
                      <p
                        style={{
                          fontSize: "11px",
                          fontWeight: 500,
                          color: "#16A34A",
                          letterSpacing: "0.02em",
                          textTransform: "uppercase",
                          marginBottom: "6px",
                        }}
                      >
                        What's Working
                      </p>
                      <p style={{ fontSize: "13px", color: "#0F172A", marginBottom: 0 }}>
                        {feedback.whatWorking || <em style={{ color: "#94A3B8" }}>—</em>}
                      </p>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div
                      style={{
                        background: "#FFFBEB",
                        border: "1px solid #FDE68A",
                        borderRadius: "8px",
                        padding: "14px 16px",
                      }}
                    >
                      <p
                        style={{
                          fontSize: "11px",
                          fontWeight: 500,
                          color: "#D97706",
                          letterSpacing: "0.02em",
                          textTransform: "uppercase",
                          marginBottom: "6px",
                        }}
                      >
                        What to Improve
                      </p>
                      <p style={{ fontSize: "13px", color: "#0F172A", marginBottom: 0 }}>
                        {feedback.whatToImprove || <em style={{ color: "#94A3B8" }}>—</em>}
                      </p>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div
                      style={{
                        background: "#EFF6FF",
                        border: "1px solid #BFDBFE",
                        borderRadius: "8px",
                        padding: "14px 16px",
                      }}
                    >
                      <p
                        style={{
                          fontSize: "11px",
                          fontWeight: 500,
                          color: "#2563EB",
                          letterSpacing: "0.02em",
                          textTransform: "uppercase",
                          marginBottom: "6px",
                        }}
                      >
                        Key Metric
                      </p>
                      <p style={{ fontSize: "13px", color: "#0F172A", marginBottom: 0 }}>
                        {feedback.keyMetric || <em style={{ color: "#94A3B8" }}>—</em>}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-3 flex-wrap">
                  <button
                    className="btn"
                    onClick={handleRevalidate}
                    disabled={isRevalidating}
                    style={{
                      background: "#8B5CF6",
                      color: "white",
                      border: "none",
                      borderRadius: "8px",
                      fontWeight: 600,
                      fontSize: "14px",
                      padding: "10px 20px",
                      cursor: isRevalidating ? "not-allowed" : "pointer",
                      transition: "all 150ms ease-out",
                    }}
                  >
                    <i className={`bi ${isRevalidating ? "bi-hourglass-split" : "bi-arrow-repeat"} me-2`} />
                    {isRevalidating ? "Re-scoring…" : "Re-validate Scores"}
                  </button>
                  <span style={{ fontSize: "12px", color: "#94A3B8" }}>
                    Updated {new Date(feedback.submittedAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
            ) : (
              /* Feedback form */
              <div>
                <div className="row g-3 mb-4">
                  <div className="col-12">
                    <label
                      style={{
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "#0F172A",
                        display: "block",
                        marginBottom: "6px",
                      }}
                    >
                      What's working well so far?
                    </label>
                    <textarea
                      rows={2}
                      value={feedbackDraft.whatWorking}
                      onChange={(e) =>
                        setFeedbackDraft((d) => ({ ...d, whatWorking: e.target.value }))
                      }
                      placeholder="e.g. Users love the onboarding flow, sign-ups are ahead of target"
                      style={{
                        width: "100%",
                        background: "#FFFFFF",
                        border: "1px solid #CBD5E1",
                        borderRadius: "8px",
                        padding: "10px 14px",
                        fontSize: "15px",
                        color: "#0F172A",
                        resize: "none",
                        outline: "none",
                      }}
                    />
                  </div>
                  <div className="col-12">
                    <label
                      style={{
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "#0F172A",
                        display: "block",
                        marginBottom: "6px",
                      }}
                    >
                      What needs improvement?
                    </label>
                    <textarea
                      rows={2}
                      value={feedbackDraft.whatToImprove}
                      onChange={(e) =>
                        setFeedbackDraft((d) => ({ ...d, whatToImprove: e.target.value }))
                      }
                      placeholder="e.g. Activation rate is low, users drop off after step 2"
                      style={{
                        width: "100%",
                        background: "#FFFFFF",
                        border: "1px solid #CBD5E1",
                        borderRadius: "8px",
                        padding: "10px 14px",
                        fontSize: "15px",
                        color: "#0F172A",
                        resize: "none",
                        outline: "none",
                      }}
                    />
                  </div>
                  <div className="col-12">
                    <label
                      style={{
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "#0F172A",
                        display: "block",
                        marginBottom: "6px",
                      }}
                    >
                      Key metric so far
                    </label>
                    <input
                      type="text"
                      value={feedbackDraft.keyMetric}
                      onChange={(e) =>
                        setFeedbackDraft((d) => ({ ...d, keyMetric: e.target.value }))
                      }
                      placeholder="e.g. 10 signups, 3 paying customers, $500 MRR"
                      style={{
                        width: "100%",
                        background: "#FFFFFF",
                        border: "1px solid #CBD5E1",
                        borderRadius: "8px",
                        padding: "10px 14px",
                        fontSize: "15px",
                        color: "#0F172A",
                        outline: "none",
                      }}
                    />
                  </div>
                </div>
                {feedbackError && (
                  <div
                    className="d-flex align-items-center mb-3 p-3"
                    style={{
                      background: "rgba(254,226,226,0.8)",
                      borderRadius: "8px",
                      border: "1px solid #FCA5A544",
                    }}
                  >
                    <i
                      className="bi bi-exclamation-circle me-2"
                      style={{ color: "#DC2626", fontSize: "16px", flexShrink: 0 }}
                    />
                    <span style={{ fontSize: "13px", color: "#991B1B" }}>{feedbackError}</span>
                  </div>
                )}
                <button
                  className="btn"
                  onClick={handleFeedbackSubmit}
                  disabled={
                    isSavingFeedback ||
                    (!feedbackDraft.whatWorking && !feedbackDraft.whatToImprove && !feedbackDraft.keyMetric)
                  }
                  style={{
                    background:
                      isSavingFeedback ||
                      (!feedbackDraft.whatWorking && !feedbackDraft.whatToImprove && !feedbackDraft.keyMetric)
                        ? "#F1F5F9"
                        : "#3B82F6",
                    color:
                      isSavingFeedback ||
                      (!feedbackDraft.whatWorking && !feedbackDraft.whatToImprove && !feedbackDraft.keyMetric)
                        ? "#94A3B8"
                        : "white",
                    border:
                      isSavingFeedback ||
                      (!feedbackDraft.whatWorking && !feedbackDraft.whatToImprove && !feedbackDraft.keyMetric)
                        ? "1px solid #E2E8F0"
                        : "none",
                    borderRadius: "8px",
                    fontWeight: 600,
                    fontSize: "14px",
                    padding: "10px 20px",
                    cursor:
                      isSavingFeedback ||
                      (!feedbackDraft.whatWorking && !feedbackDraft.whatToImprove && !feedbackDraft.keyMetric)
                        ? "not-allowed"
                        : "pointer",
                    transition: "all 150ms ease-out",
                  }}
                >
                  <i className={`bi ${isSavingFeedback ? "bi-hourglass-split" : "bi-check2-circle"} me-2`} />
                  {isSavingFeedback ? "Saving…" : "Save Feedback"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      {/* end::Feedback Capture Widget */}

      {/* begin::Build Failed Banner */}
      {buildStatus === "failed" && (
        <div
          className="card mb-5"
          style={{
            background: "linear-gradient(135deg, #FFF1F2 0%, #FFE4E6 100%)",
            border: "1px solid #EF4444",
            borderRadius: "12px",
            boxShadow: "0 4px 6px -1px rgba(239,68,68,0.1)",
          }}
        >
          <div className="card-body py-4 px-6">
            <div className="d-flex align-items-center gap-4">
              <div
                style={{
                  width: "44px",
                  height: "44px",
                  background: "#EF4444",
                  borderRadius: "9999px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <i className="bi bi-exclamation-circle-fill" style={{ color: "white", fontSize: "20px" }} />
              </div>
              <div className="flex-grow-1">
                <h4 style={{ fontSize: "16px", fontWeight: 600, color: "#7F1D1D", marginBottom: "2px" }}>
                  Build Failed
                </h4>
                <p style={{ fontSize: "13px", color: "#B91C1C", marginBottom: 0 }}>
                  The build pipeline encountered an error. Check the pipeline logs, then click Build It to try again.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* end::Build Failed Banner */}

      {/* begin::Milestone Banner */}
      <MilestoneBanner
        className="mb-5"
        stepsStarted={progressData.stepsStarted}
        projectProgress={progressData.projectProgress}
        categoryScores={categoryScores}
        onToolOpen={(toolId) => {
          switch (toolId) {
            case "branding": navigate("/branding-setup", { state: { projectTitle, projectID, projectDescription, userID } }); break;
            case "logo": setShowLogoModal(true); break;
            case "marketing": setShowMarketingModal(true); break;
            case "website": setShowWebsiteModal(true); break;
            case "survey": setShowSurveyModal(true); break;
            case "mvp": setShowMVPModal(true); break;
            case "seo": setShowSEOModal(true); break;
            case "content": setShowContentModal(true); break;
            case "email": setShowEmailModal(true); break;
            case "social": setShowSocialModal(true); break;
            case "persona": setShowPersonaTestModal(true); break;
          }
        }}
      />
      {/* end::Milestone Banner */}

      {/* begin::Next Steps + Business Score (50/50) */}
      <div className="row g-5 mb-5">
        <div className="col-xl-6">
          <RecommendedNextStep
            className="card-stretch h-100"
            userID={userID}
            projectID={projectID}
            projectTitle={projectTitle}
            projectProgress={progressData.projectProgress}
            categoryScores={categoryScores}
          />
        </div>
        <div className="col-xl-6" id="business-score-widget">
          <BusinessScoreWidget className="h-100" projectId={projectID} />
        </div>
      </div>
      {/* end::Next Steps + Business Score */}

      {/* begin::Export Row */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center gap-2">
          <h5 style={{ margin: 0, fontSize: "15px", fontWeight: 600, color: "#0F172A" }}>
            Validation Steps
          </h5>
          {/* Status badge selector */}
          <div style={{ position: "relative" }}>
            <button
              onClick={() => setShowStatusDropdown((v) => !v)}
              style={{
                background: currentStatusStyle ? currentStatusStyle.bg : "#F1F5F9",
                color: currentStatusStyle ? currentStatusStyle.color : "#94A3B8",
                border: "none",
                borderRadius: "9999px",
                fontSize: "11px",
                fontWeight: 500,
                padding: "3px 10px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "4px",
                letterSpacing: "0.02em",
              }}
              title="Set project status"
            >
              {projectStatus ?? "Set status"}
              <i className="bi bi-chevron-down" style={{ fontSize: 9 }}></i>
            </button>
            {showStatusDropdown && (
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 4px)",
                  left: 0,
                  zIndex: 100,
                  background: "#FFFFFF",
                  border: "1px solid #E2E8F0",
                  borderRadius: "8px",
                  boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)",
                  minWidth: "130px",
                  padding: "4px 0",
                }}
              >
                {STATUS_OPTIONS_DASH.map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => handleDashStatusChange(opt.key)}
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "7px 12px",
                      background: projectStatus === opt.key ? opt.bg : "transparent",
                      border: "none",
                      cursor: "pointer",
                      fontSize: "13px",
                      fontWeight: projectStatus === opt.key ? 600 : 500,
                      color: opt.color,
                      textAlign: "left",
                    }}
                    onMouseEnter={(e) => {
                      if (projectStatus !== opt.key)
                        e.currentTarget.style.background = opt.bg;
                    }}
                    onMouseLeave={(e) => {
                      if (projectStatus !== opt.key)
                        e.currentTarget.style.background = "transparent";
                    }}
                  >
                    <span
                      style={{
                        width: "8px",
                        height: "8px",
                        borderRadius: "50%",
                        background: opt.color,
                        flexShrink: 0,
                      }}
                    />
                    {opt.key}
                    {projectStatus === opt.key && (
                      <i className="bi bi-check2 ms-auto" style={{ fontSize: "13px", color: opt.color }}></i>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="d-flex align-items-center gap-3 flex-wrap">
          {pdfError && (
            <span style={{ fontSize: "13px", color: "#EF4444" }}>
              <i className="bi bi-exclamation-circle me-1" />
              {pdfError}
            </span>
          )}
          {/* Share with Team — Agency tier only, visible when user has a team */}
          {isAgency && team && !teamLoading && (
            <button
              className="btn"
              onClick={() => setShowShareModal(true)}
              title="Share this project with your team members"
              style={{
                background: projectCollaborators.length > 0 ? "#EDE9FE" : "transparent",
                color: projectCollaborators.length > 0 ? "#6D28D9" : "#475569",
                border: `1px solid ${projectCollaborators.length > 0 ? "#8B5CF6" : "#CBD5E1"}`,
                borderRadius: "8px",
                fontWeight: 600,
                fontSize: "13px",
                padding: "7px 14px",
                cursor: "pointer",
                transition: "all 150ms ease-out",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <i className="bi bi-people" />
              {projectCollaborators.length > 0
                ? `Shared (${projectCollaborators.length})`
                : "Share with Team"}
            </button>
          )}
          <button
            className="btn"
            onClick={() => exportPDF(userID, projectID)}
            disabled={isExporting || !canExportPDF}
            title={
              !canExportPDF
                ? "PDF Export requires a Pro subscription"
                : "Download your full validation report as PDF"
            }
            style={{
              background: "transparent",
              color: canExportPDF ? "#3B82F6" : "#94A3B8",
              border: `1px solid ${canExportPDF ? "#3B82F6" : "#E2E8F0"}`,
              borderRadius: "8px",
              fontWeight: 600,
              fontSize: "13px",
              padding: "7px 14px",
              cursor: canExportPDF && !isExporting ? "pointer" : "not-allowed",
              transition: "all 150ms ease-out",
            }}
          >
            <i
              className={`bi ${isExporting ? "bi-hourglass-split" : "bi-file-earmark-pdf"} me-2`}
            />
            {isExporting ? "Generating…" : "Export Report"}
            {!canExportPDF && (
              <span style={{ fontSize: "11px", marginLeft: "4px", color: "#94A3B8" }}>
                — Pro
              </span>
            )}
          </button>
        </div>
      </div>
      {/* end::Export Row */}

      {/* begin::Score Breakdown Panel */}
      {categoryScores && (
        <ErrorBoundary fallback={<WidgetErrorFallback />}>
          <ScoreBreakdownPanel
            categoryScores={categoryScores}
            completionMap={stepCompletionMap}
            onStepClick={(stepNum) =>
              navigate(`/step${stepNum}`, {
                state: { projectTitle, projectID, projectDescription, userID },
              })
            }
            isTeamProject={isTeamProject}
            stepAssignments={stepAssignments}
          />
        </ErrorBoundary>
      )}
      {/* end::Score Breakdown Panel */}

      {/* begin::Score Trend Chart */}
      <ErrorBoundary fallback={<WidgetErrorFallback />}>
        <ScoreTrendChart history={scoreHistory} />
      </ErrorBoundary>
      {/* end::Score Trend Chart */}

      {/* begin::Activity Calendar */}
      <ErrorBoundary fallback={<WidgetErrorFallback />}>
        <ActivityCalendar activityMap={stepActivityMap} />
      </ErrorBoundary>
      {/* end::Activity Calendar */}

      {/* begin::Streak Badge */}
      {(() => {
        const streak = calculateStreak(stepActivityMap);
        if (streak < 2) return null;
        return (
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              background: "#FEF3C7",
              border: "1px solid #F59E0B",
              borderRadius: 4,
              padding: "4px 12px",
              fontSize: 11,
              fontWeight: 700,
              color: "#92400E",
              letterSpacing: "0.02em",
              marginBottom: 16,
            }}
          >
            <i className="bi bi-lightning-charge-fill" style={{ fontSize: 13, color: "#F59E0B" }}></i>
            {streak} day streak
          </div>
        );
      })()}
      {/* end::Streak Badge */}

      {/* begin::Step Mini-Map */}
      <ErrorBoundary fallback={<WidgetErrorFallback />}>
        <StepMiniMap
          completionMap={stepCompletionMap}
          notesMap={stepNotesMap}
          onStepClick={(stepNum) =>
            navigate(`/step${stepNum}`, {
              state: { projectTitle, projectID, projectDescription, userID },
            })
          }
        />
      </ErrorBoundary>
      {/* end::Step Mini-Map */}

      {/* begin::Recent Activity */}
      <ErrorBoundary fallback={<WidgetErrorFallback />}>
        <RecentActivity
          activityMap={stepActivityMap}
          onStepClick={(stepNum) =>
            navigate(`/step${stepNum}`, {
              state: { projectTitle, projectID, projectDescription, userID },
            })
          }
        />
      </ErrorBoundary>
      {/* end::Recent Activity */}

      {/* begin::Team Progress Chart (team projects only) */}
      {isTeamProject && (
        <ErrorBoundary fallback={<WidgetErrorFallback />}>
          <TeamProgressChart
            stepCompletionMap={stepCompletionMap}
            collaborators={projectCollaborators}
            stepAssignments={stepAssignments}
          />
        </ErrorBoundary>
      )}
      {/* end::Team Progress Chart */}

      {/* begin::Team Activity Feed (team projects only) */}
      {isAgency && team && (
        <ErrorBoundary fallback={<WidgetErrorFallback />}>
          <TeamActivityFeed teamId={team.id} projectID={projectID} />
        </ErrorBoundary>
      )}
      {/* end::Team Activity Feed */}

      {/* begin::Steps List (full width) */}
      <ErrorBoundary fallback={<WidgetErrorFallback />}>
        <StepsList
          className="card-stretch"
          userID={userID}
          categoryScores={categoryScores}
          categoryReasons={categoryReasons}
        />
      </ErrorBoundary>
      {/* end::Steps List */}

      {/* begin::Modals */}
      <CreateAppModal
        show={showCreateAppModal}
        handleClose={() => setShowCreateAppModal(false)}
        userID={userID}
      />
      <LogoGeneratorModal
        show={showLogoModal}
        handleClose={() => setShowLogoModal(false)}
        projectTitle={projectTitle}
        projectDescription={projectDescription}
        generateLogo={(prompt, style) => generateLogo(prompt, style, projectTitle)}
        isGenerating={isGeneratingLogo}
        error={logoError}
        onLogoGenerated={(logoUrl) => {
          saveLogo(logoUrl);
        }}
      />
      <WebsiteGeneratorModal
        show={showWebsiteModal}
        handleClose={() => setShowWebsiteModal(false)}
        projectId={projectID}
        projectTitle={projectTitle}
        projectDescription={projectDescription}
      />
      <SurveyBuilderModal
        show={showSurveyModal}
        handleClose={() => setShowSurveyModal(false)}
        projectId={projectID}
        projectTitle={projectTitle}
      />
      <MarketingPlanModal
        show={showMarketingModal}
        handleClose={() => setShowMarketingModal(false)}
        projectId={projectID}
        projectTitle={projectTitle}
      />
      <MVPScaffolderModal
        show={showMVPModal}
        handleClose={() => setShowMVPModal(false)}
        projectId={projectID}
        projectTitle={projectTitle}
      />
      <SEOAgentModal
        show={showSEOModal}
        handleClose={() => setShowSEOModal(false)}
        projectId={projectID}
        projectTitle={projectTitle}
      />
      <ContentCreatorModal
        show={showContentModal}
        handleClose={() => setShowContentModal(false)}
        projectId={projectID}
        projectTitle={projectTitle}
      />
      <EmailSequenceModal
        show={showEmailModal}
        handleClose={() => setShowEmailModal(false)}
        projectId={projectID}
        projectTitle={projectTitle}
      />
      <SocialAssetsModal
        show={showSocialModal}
        handleClose={() => setShowSocialModal(false)}
        projectId={projectID}
        projectTitle={projectTitle}
      />
      <PersonaTestModal
        show={showPersonaTestModal}
        handleClose={() => setShowPersonaTestModal(false)}
        projectId={projectID}
        projectTitle={projectTitle}
      />
      {/* Share with Team modal */}
      {showShareModal && team && (
        <ShareProjectModal
          team={team}
          ownerUID={userID}
          projectID={projectID}
          projectTitle={projectTitle}
          currentCollaborators={projectCollaborators}
          onClose={() => setShowShareModal(false)}
          onShared={(updatedCollaborators) => {
            setProjectCollaborators(updatedCollaborators);
            setShowShareModal(false);
          }}
        />
      )}
      {/* end::Modals */}
    </>
  );
};
